// ABOUTME: Custom React hook for authentication state management and user session handling
// ABOUTME: Provides easy access to auth state, user data, and authentication actions from Redux store

import { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ApplicationState } from '../../store';
import {
  login as authActions_login,
  logout as authActions_logout,
  reauth as authActions_reauth,
  acceptTerms as authActions_acceptTerms,
  setUsersUni as authActions_setUsersUni,
  setUsersIntake as authActions_setUsersIntake,
  getCurrentUserRequest,
  getAuthAgencyRequest,
} from '../../store/auth/actions';
import { UserRole } from '../../store/auth/types';
import { IUser } from '../../types';

export interface UseAuthReturn {
  // Authentication state
  isAuthenticated: boolean;
  isAuthenticating: boolean;
  isLoggedIn: boolean;

  // User data
  user: IUser | null;
  userRole: UserRole;
  email: string;
  studentNumber: string;
  uni: string;
  pathway?: string;
  intake: string;
  institution: string;
  groups: string[];

  // Permissions and access
  isPathway: boolean;
  pathwayAccess: 'Full' | 'Partial' | 'None';
  terms: boolean;
  marketing: boolean;

  // Session state
  wsConnected: boolean;
  isTesting: boolean;

  // Error states
  validationErrors: ReadonlyArray<{ field: string; message: string }> | undefined;
  serverErrors: ReadonlyArray<string> | undefined;
  authAgencyError: string | null;

  // Agency data
  authAgencyData: any | null;
  authAgencyLoading: boolean;
  agencyId: string;

  // Authentication actions
  login: (userName: string, password: string) => void;
  logout: (redirect?: boolean) => void;
  reauth: () => void;
  acceptTerms: () => void;
  setUsersUni: (value: string) => void;
  setUsersIntake: (value: string) => void;
  getCurrentUser: () => void;
  getAuthAgency: (agencyId: string, checkApplyAccess?: boolean) => void;

  // Role checks
  hasRole: (role: UserRole | UserRole[]) => boolean;
  isAdmin: boolean;
  isUniAdmin: boolean;
  isUniUser: boolean;
  isAgent: boolean;
  isEnrolyAgent: boolean;
  isPartner: boolean;
  isImmigrationProvider: boolean;

  // Permission checks
  canViewStudents: boolean;
  canEditStudents: boolean;
  canManageUsers: boolean;
  canAccessFinancials: boolean;
}

/**
 * Custom hook for authentication state management
 * Provides centralized access to authentication state and actions
 */
export const useAuth = (): UseAuthReturn => {
  const dispatch = useDispatch();
  const authState = useSelector((state: ApplicationState) => state.auth);

  // Authentication actions
  const login = useCallback(
    (userName: string, password: string) => {
      dispatch(authActions_login(userName, password));
    },
    [dispatch],
  );

  const logout = useCallback(
    (redirect = true) => {
      dispatch(authActions_logout(redirect));
    },
    [dispatch],
  );

  const reauth = useCallback(() => {
    dispatch(authActions_reauth());
  }, [dispatch]);

  const acceptTerms = useCallback(() => {
    dispatch(authActions_acceptTerms());
  }, [dispatch]);

  const setUsersUni = useCallback(
    (value: string) => {
      dispatch(authActions_setUsersUni(value));
    },
    [dispatch],
  );

  const setUsersIntake = useCallback(
    (value: string) => {
      dispatch(authActions_setUsersIntake(value));
    },
    [dispatch],
  );

  const getCurrentUser = useCallback(() => {
    dispatch(getCurrentUserRequest());
  }, [dispatch]);

  const getAuthAgency = useCallback(
    (agencyId: string, checkApplyAccess?: boolean) => {
      dispatch(getAuthAgencyRequest(agencyId, checkApplyAccess));
    },
    [dispatch],
  );

  // Role checking utility
  const hasRole = useCallback(
    (role: UserRole | UserRole[]): boolean => {
      if (Array.isArray(role)) {
        return role.includes(authState.role);
      }
      return authState.role === role;
    },
    [authState.role],
  );

  // Derived state
  const isAuthenticated = Boolean(authState.isLoggedIn && authState.currentUser);
  const isAdmin = hasRole(UserRole.Admin);
  const isUniAdmin = hasRole(UserRole.UniAdmin);
  const isUniUser = hasRole([UserRole.Uni, UserRole.UniSecure, UserRole.UniLite, UserRole.UniAdmin]);
  const isAgent = hasRole([UserRole.Agent, UserRole.AgencyAdmin]);
  const isEnrolyAgent = hasRole([UserRole.Enroly, UserRole.Admin]);
  const isPartner = hasRole(UserRole.Partner);
  const isImmigrationProvider = hasRole(UserRole.ImmigrationProvider);

  // Permission-based checks
  const canViewStudents = isAuthenticated && (isAdmin || isUniUser || isAgent || isEnrolyAgent);
  const canEditStudents = isAuthenticated && (isAdmin || isUniAdmin || isUniUser);
  const canManageUsers = isAuthenticated && (isAdmin || isUniAdmin);
  const canAccessFinancials = isAuthenticated && (isAdmin || isUniAdmin || hasRole(UserRole.UniSecure));

  return {
    // Authentication state
    isAuthenticated,
    isAuthenticating: authState.isAuthenticating,
    isLoggedIn: Boolean(authState.isLoggedIn),

    // User data
    user: authState.currentUser,
    userRole: authState.role,
    email: authState.email,
    studentNumber: authState.student_number,
    uni: authState.uni,
    pathway: authState.pathway,
    intake: authState.intake,
    institution: authState.institution,
    groups: authState.groups,

    // Permissions and access
    isPathway: Boolean(authState.isPathway),
    pathwayAccess: authState.pathwayAccess,
    terms: authState.terms,
    marketing: authState.marketing,

    // Session state
    wsConnected: Boolean(authState.wsConnected),
    isTesting: Boolean(authState.isTesting),

    // Error states
    validationErrors: authState.validation_errors,
    serverErrors: authState.server_errors,
    authAgencyError: authState.authAgencyError,

    // Agency data
    authAgencyData: authState.authAgencyData,
    authAgencyLoading: authState.authAgencyLoading,
    agencyId: authState.agencyId,

    // Authentication actions
    login,
    logout,
    reauth,
    acceptTerms,
    setUsersUni,
    setUsersIntake,
    getCurrentUser,
    getAuthAgency,

    // Role checks
    hasRole,
    isAdmin,
    isUniAdmin,
    isUniUser,
    isAgent,
    isEnrolyAgent,
    isPartner,
    isImmigrationProvider,

    // Permission checks
    canViewStudents,
    canEditStudents,
    canManageUsers,
    canAccessFinancials,
  };
};

export default useAuth;
